import classNames from 'classnames';
import React, { cloneElement, memo } from 'react';
import ReactTooltip from 'react-tooltip';
import { createPortal } from 'react-dom';

import { useIdOrRandomString } from 'src/utils/formHelper';
import { DOM_ELEMENT_FOR_PORTALS } from 'src/config';
import { ChildrenPropType } from 'src/utils/types';

export type UiHoverTooltipProps = {
  children: React.ReactElement;

  text?: string | React.ReactNode;
  customBody?: ChildrenPropType;

  header?: string;
  className?: string;
  tooltipClassName?: string;
  place?: 'top' | 'right' | 'bottom' | 'left';
  id?: string | number;
  noWrapper?: boolean; // use this more often
  hoverTextCommonStyle?: boolean;
  render?: boolean; // in case when it's not necessary to render the tooltip but changing the code is imppossible
  delayHide?: number;
  withPortal?: boolean;
};

/**
 * a component for displaying any text but if given text is lengthy, it shows a tooltip over the text
 */
export const UiHoverTooltip: React.FC<UiHoverTooltipProps> = memo(
  ({
    children,
    text,
    header,
    className,
    place,
    id: propId,
    tooltipClassName,
    noWrapper = false,
    hoverTextCommonStyle = false,
    render = true,
    delayHide = 200,
    withPortal,
    customBody,
  }) => {
    const id = useIdOrRandomString(propId);

    if (!render) {
      return children;
    }

    const elementProps = { 'data-tip': '', 'data-for': id, 'data-delay-hide': delayHide };

    const component = () => (
      <ReactTooltip
        id={id}
        className={classNames('r-tooltip__tooltip', tooltipClassName, {
          'tt-initial': hoverTextCommonStyle,
        })}
        effect="solid"
        place={place}
        delayHide={delayHide}
        delayShow={0}
      >
        {customBody || (
          <>
            {header && <div className="r-title">{header}</div>}
            <span>{text}</span>
          </>
        )}
      </ReactTooltip>
    );

    return (
      <>
        {noWrapper ? (
          cloneElement(children, elementProps)
        ) : (
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          <div {...elementProps} className={classNames('r-tooltip r-tooltip-hover', className)}>
            {children}
          </div>
        )}
        {render &&
          (customBody || text) &&
          /* eslint-disable-next-line no-underscore-dangle */
          (withPortal && !window._useTestModals ? createPortal(component(), DOM_ELEMENT_FOR_PORTALS) : component())}
      </>
    );
  }
);
